import { useEffect, useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import {
  MessageSquare, Search, Send, Loader2, User, Phone, ArrowUp,
  ArrowDown, Clock, CheckCircle2, RefreshCcw, MessageCircle, ArrowLeft
} from "lucide-react";

interface ConversationPreview {
  customer_id: string;
  customer_name: string;
  customer_phone: string | null;
  last_message: string;
  last_message_at: string;
  direction: string;
  unread_count: number;
}

interface Message {
  id: string;
  direction: string;
  body: string;
  created_at: string;
  status: string;
  read_at: string | null;
}

export default function CommunicationHub() {
  const [conversations, setConversations] = useState<ConversationPreview[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    if (selectedCustomerId) {
      loadMessages(selectedCustomerId);
      markAsRead(selectedCustomerId);
    }
  }, [selectedCustomerId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Real-time subscription
  useEffect(() => {
    const channel = supabase
      .channel('schema-db-changes')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        (payload) => {
          const newMessage = payload.new as Message;
          // Refresh conversations list to update previews
          loadConversations();
          // If the message belongs to the current conversation, add it
          if (newMessage.customer_id === selectedCustomerId) {
            setMessages(prev => [...prev, newMessage]);
            markAsRead(newMessage.customer_id);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedCustomerId]);

  const loadConversations = async () => {
    setIsLoading(true);

    // Get all customers who have messages
    const { data: msgData } = await supabase
      .from("messages")
      .select("customer_id, body, direction, created_at, read_at")
      .order("created_at", { ascending: false })
      .limit(500);

    if (!msgData || msgData.length === 0) {
      setConversations([]);
      setIsLoading(false);
      return;
    }

    // Group by customer
    const grouped: Record<string, {
      messages: typeof msgData;
      unread: number;
    }> = {};

    msgData.forEach(msg => {
      if (!grouped[msg.customer_id]) {
        grouped[msg.customer_id] = { messages: [], unread: 0 };
      }
      grouped[msg.customer_id].messages.push(msg);
      if (msg.direction === "inbound" && !msg.read_at) {
        grouped[msg.customer_id].unread++;
      }
    });

    // Get customer details
    const customerIds = Object.keys(grouped);
    const { data: customers } = await supabase
      .from("customers")
      .select("id, first_name, last_name, phone")
      .in("id", customerIds);

    const customerMap: Record<string, { id: string; first_name: string; last_name: string; phone: string | null }> = {};
    (customers || []).forEach(c => { customerMap[c.id] = c; });

    const convos: ConversationPreview[] = customerIds
      .map(cid => {
        const c = customerMap[cid];
        const latest = grouped[cid].messages[0];
        return {
          customer_id: cid,
          customer_name: c ? `${c.first_name} ${c.last_name}` : "Unknown",
          customer_phone: c?.phone || null,
          last_message: latest.body,
          last_message_at: latest.created_at,
          direction: latest.direction,
          unread_count: grouped[cid].unread,
        };
      })
      .sort((a, b) => new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime());

    setConversations(convos);
    setIsLoading(false);
  };

  const loadMessages = async (customerId: string) => {
    const { data } = await supabase
      .from("messages")
      .select("id, direction, body, created_at, status, read_at")
      .eq("customer_id", customerId)
      .order("created_at", { ascending: false })
      .limit(100);

    // Reverse so oldest is first (chat view), but we only loaded the last 100
    setMessages((data || []).reverse());
  };

  const markAsRead = async (customerId: string) => {
    await supabase
      .from("messages")
      .update({ read_at: new Date().toISOString() })
      .eq("customer_id", customerId)
      .eq("direction", "inbound")
      .is("read_at", null);
    
    // Update conversation unread count locally
    setConversations(prev =>
      prev.map(c => c.customer_id === customerId ? { ...c, unread_count: 0 } : c)
    );
  };

  const handleSendMessage = async () => {
    if (!selectedCustomerId || !newMessage.trim()) return;

    const convo = conversations.find(c => c.customer_id === selectedCustomerId);
    if (!convo?.customer_phone) {
      toast.error("Customer has no phone number");
      return;
    }

    setIsSending(true);
    try {
      const { data: userData } = await supabase.auth.getUser();

      const { data, error } = await supabase.functions.invoke("send-sms", {
        body: {
          to: convo.customer_phone,
          body: newMessage.trim(),
          customer_id: selectedCustomerId,
          sent_by: userData?.user?.id || null,
        },
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || "Failed to send");

      setNewMessage("");
      await loadMessages(selectedCustomerId);
      await loadConversations();
    } catch (err: any) {
      toast.error(err.message || "Failed to send SMS");
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const selectedConvo = conversations.find(c => c.customer_id === selectedCustomerId);
  const totalUnread = conversations.reduce((acc, c) => acc + c.unread_count, 0);

  const filteredConversations = conversations.filter(c =>
    c.customer_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.customer_phone?.includes(searchQuery)
  );

  return (
    <div className="space-y-6 pb-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-3xl font-heading font-bold flex items-center gap-2">
            <MessageSquare className="h-8 w-8" />
            Messages
          </h1>
          <p className="text-muted-foreground">
            Two-way SMS communication hub
            {totalUnread > 0 && (
              <Badge className="ml-2 bg-red-500 text-white">{totalUnread} unread</Badge>
            )}
          </p>
        </div>
        <Button variant="outline" onClick={() => { loadConversations(); if (selectedCustomerId) loadMessages(selectedCustomerId); }}>
          <RefreshCcw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      <div className="flex flex-col lg:grid lg:grid-cols-3 gap-4 h-[calc(100vh-220px)] min-h-[500px]">
        {/* Conversations List */}
        <Card className={`lg:col-span-1 flex-col overflow-hidden ${selectedCustomerId ? 'hidden lg:flex' : 'flex'} h-full`}>
          <CardHeader className="pb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search conversations..."
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </CardHeader>
          <CardContent className="flex-1 overflow-y-auto p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="text-center py-12 px-4">
                <MessageCircle className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
                <p className="text-sm text-muted-foreground">No conversations yet</p>
                <p className="text-xs text-muted-foreground mt-1">Send an SMS from a customer profile to start</p>
              </div>
            ) : (
              filteredConversations.map(convo => (
                <div
                  key={convo.customer_id}
                  onClick={() => setSelectedCustomerId(convo.customer_id)}
                  className={`px-4 py-3 border-b cursor-pointer transition-colors hover:bg-muted/50 ${
                    selectedCustomerId === convo.customer_id ? 'bg-primary/5 border-l-2 border-l-primary' : ''
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-sm truncate flex-1">{convo.customer_name}</span>
                    <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                      {convo.unread_count > 0 && (
                        <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0">{convo.unread_count}</Badge>
                      )}
                      <span className="text-[10px] text-muted-foreground">
                        {formatDistanceToNow(new Date(convo.last_message_at), { addSuffix: true })}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {convo.direction === "inbound" ? (
                      <ArrowDown className="h-3 w-3 text-blue-500 flex-shrink-0" />
                    ) : (
                      <ArrowUp className="h-3 w-3 text-green-500 flex-shrink-0" />
                    )}
                    <p className="text-xs text-muted-foreground truncate">{convo.last_message}</p>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Message Thread */}
        <Card className={`lg:col-span-2 flex-col overflow-hidden ${!selectedCustomerId ? 'hidden lg:flex' : 'flex'} h-full`}>
          {!selectedCustomerId ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="h-16 w-16 mx-auto text-muted-foreground/20 mb-4" />
                <p className="text-lg font-medium text-muted-foreground">Select a conversation</p>
                <p className="text-sm text-muted-foreground">Choose a customer from the list to view messages</p>
              </div>
            </div>
          ) : (
            <>
              {/* Header */}
              <CardHeader className="pb-3 border-b shrink-0">
                <div className="flex items-center gap-3">
                  <Button variant="ghost" size="icon" className="lg:hidden -ml-2 shrink-0" onClick={() => setSelectedCustomerId(null)}>
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-base">{selectedConvo?.customer_name}</CardTitle>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Phone className="h-3 w-3" />
                      <span>{selectedConvo?.customer_phone || "No phone"}</span>
                    </div>
                  </div>
                </div>
              </CardHeader>

              {/* Messages */}
              <CardContent className="flex-1 overflow-y-auto py-4 space-y-3">
                {messages.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-sm text-muted-foreground">No messages in this conversation</p>
                  </div>
                ) : (
                  messages.map(msg => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.direction === "outbound" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                          msg.direction === "outbound"
                            ? "bg-primary text-primary-foreground rounded-br-sm"
                            : "bg-muted rounded-bl-sm"
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap break-words">{msg.body}</p>
                        <div className={`flex items-center gap-1.5 mt-1 ${
                          msg.direction === "outbound" ? "justify-end" : ""
                        }`}>
                          <span className={`text-[10px] ${
                            msg.direction === "outbound" ? "text-primary-foreground/60" : "text-muted-foreground"
                          }`}>
                            {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true })}
                          </span>
                          {msg.direction === "outbound" && msg.status === "sent" && (
                            <CheckCircle2 className="h-3 w-3 text-primary-foreground/60" />
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </CardContent>

              {/* Compose */}
              <div className="border-t p-4">
                <div className="flex gap-2">
                  <Textarea
                    placeholder="Type a message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    rows={1}
                    className="resize-none min-h-[40px] max-h-[100px]"
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={isSending || !newMessage.trim()}
                    size="icon"
                    className="h-10 w-10 flex-shrink-0"
                  >
                    {isSending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  Press Enter to send, Shift+Enter for new line
                </p>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
